import React from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Dropdown, type DropdownProps, Icon } from 'semantic-ui-react';
import { useToasts } from 'common/toast';
import ThemeTitleIcon from '@/common/components/ThemeTitleIcon';
import { sentimentForSignal } from '@/areas/culture-hub/components/interventions/action-planner/sentiment';
import {
  useEmergentThemeTeamDetailQuery,
  SuggestedIntervention,
} from '@/areas/culture-hub/components/interventions/action-planner/api';
import { Recommendation } from '@/areas/culture-hub/types/api';
import { useCultureHubQueryParams } from '@/areas/culture-hub/hooks/useCultureHubQueryParams';
import { useActionPlanOperations } from '@/areas/culture-hub/hooks/useActionPlanOperations';
import { ACTION_PLAN_TYPE_INTERVENTIONS } from '@/areas/culture-hub/constants';
import {
  addRecommendationToPlan,
  getSelectedActionPlanTaskKeys,
} from '@/areas/culture-hub/slices/cultureHubInterventionsSlice';
import {
  getAssessmentPeriods,
  getCurrentOrganizationId,
  getLeadershipTeamsForSelectedAssessmentPeriod,
  getMemberId,
  getSelectedAssessmentPeriodId,
  isCurrentMemberTeamLeader,
} from '@/common/selectors/auth';
import userContextSlice from '@/common/slices/userContextSlice';
import { TopBarContext } from '@/common/layouts/AuthLayout/TopBarContext';
import MobileTopBarSelectors from '@/mobile/components/MobileTopBarSelectors';
import MobileAssessmentPeriodList from '@/mobile/areas/dashboard/MobileAssessmentPeriodList';
import MobileRecommendationTabContent from './recommendations/MobileRecommendationTabContent';
import {
  MobilePageContent,
  MobilePageSection,
  SectionDescription,
  SectionHeader,
  Title,
  MobileSpinner,
} from '@/mobile/layouts/common/MobilePageStyles';

const getThemeAccentColor = (variant: string): string => {
  if (variant === 'very-positive' || variant === 'positive') return '#16a34a';
  if (variant === 'neutral') return '#0284c7';
  return '#dc2626';
};

const convertSuggestedInterventionToRecommendation = (
  suggestion: SuggestedIntervention
): Recommendation => ({
  ...suggestion,
  recommendation_template_id: suggestion.recommendation_template_id,
  recommendation_name: suggestion.recommendation_name,
  task_index: suggestion.task_index,
  task: suggestion.task,
  description: suggestion.description ?? undefined,
  time: suggestion.time ?? undefined,
  time_estimate: suggestion.time_estimate ?? undefined,
  practicalExample: suggestion.practicalExample ?? suggestion.practical_example ?? undefined,
  practical_example: suggestion.practical_example ?? undefined,
  softSkills: suggestion.softSkills ?? suggestion.soft_skills ?? undefined,
  areaOfFocus: suggestion.areaOfFocus ?? suggestion.area_of_focus ?? undefined,
  processChangeImplementationTime:
    suggestion.processChangeImplementationTime ?? suggestion.process_change_implementation_time ?? undefined,
  howToImplement: suggestion.howToImplement ?? suggestion.how_to_implement ?? undefined,
  whatNotToDo: suggestion.whatNotToDo ?? suggestion.what_not_to_do ?? undefined,
  howToKnowItsWorking: suggestion.howToKnowItsWorking ?? suggestion.how_to_know_its_working ?? undefined,
  suggestedProcessChanges:
    suggestion.suggestedProcessChanges ??
    suggestion.suggested_process_changes ??
    suggestion.processChanges ??
    suggestion.process_changes ??
    undefined,
  references: suggestion.references ?? undefined,
  supportingScience: suggestion.supportingScience ?? suggestion.supporting_science ?? undefined,
  content: Array.isArray(suggestion.content)
    ? suggestion.content
    : (Array.isArray(suggestion.resources) ? suggestion.resources : undefined),
  why_question: suggestion.why_question ?? undefined,
  relevance_score: suggestion.relevance_score,
  relevance_label: suggestion.relevance_label,
});

const MobileFocusPlanRecommendations: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToast } = useToasts();

  const { assessmentPeriodId, teamId, organizationId, shouldFetchData, isMostRecentClosedAssessmentPeriod } = useCultureHubQueryParams();
  const currentUserId = useSelector(getMemberId) ?? null;
  const isTeamLeader = useSelector(isCurrentMemberTeamLeader);
  const selectedActionPlanTaskKeys = useSelector(getSelectedActionPlanTaskKeys);

  const assessmentPeriods = useSelector(getAssessmentPeriods);
  const selectedTeamId = useSelector(userContextSlice.selectors.getSelectedTeamId as any) as number | undefined;
  const selectedAssessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const currentOrganizationId = useSelector(getCurrentOrganizationId);
  const leadershipTeams = useSelector(getLeadershipTeamsForSelectedAssessmentPeriod);
  const { setTopBarContent } = React.useContext(TopBarContext);

  const shouldFetchThemeDetail = !!assessmentPeriodId && !!teamId && !!organizationId && !!shouldFetchData;

  const { data, isLoading, isFetching, isError } = useEmergentThemeTeamDetailQuery(
    {
      assessmentPeriodId: assessmentPeriodId as number,
      teamId: teamId as number,
    },
    {
      skip: !shouldFetchThemeDetail,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
    }
  );

  const { saveAllTasks, determineLeaderAndCategory, isSaving } = useActionPlanOperations({
    selectedAssessmentPeriodId: assessmentPeriodId,
    organizationId,
    currentUserId,
    currentUserDebriefSchedule: null,
    categoryDelegates: [],
    existingActionPlans: undefined,
    actionPlanType: ACTION_PLAN_TYPE_INTERVENTIONS,
    teamId,
  });

  const handleTeamChange = React.useCallback(
    (value: string | number | undefined) => {
      const nextTeamId = typeof value === 'string' ? Number(value) : value;
      if (nextTeamId && nextTeamId !== selectedTeamId) {
        dispatch(userContextSlice.actions.setSelectedTeamId(nextTeamId));
      }
    },
    [dispatch, selectedTeamId]
  );

  const handleAssessmentPeriodChange = React.useCallback(
    (value: string | number | undefined, period?: any) => {
      const selected = period ?? assessmentPeriods?.find((p: any) => p.id === value);
      if (selected && selectedAssessmentPeriodId !== selected.id) {
        dispatch(
          userContextSlice.actions.setSelectedAssessmentPeriod({
            selectedAssessmentPeriod: selected,
            assessmentPeriods,
          })
        );
      }
    },
    [assessmentPeriods, dispatch, selectedAssessmentPeriodId]
  );

  React.useEffect(() => {
    if ((selectedTeamId === undefined || selectedTeamId === null) && teamId != null) {
      dispatch(userContextSlice.actions.setSelectedTeamId(teamId));
    }
  }, [dispatch, selectedTeamId, teamId]);

  React.useEffect(() => {
    setTopBarContent(
      <MobileTopBarSelectors
        teamDropdown={
          leadershipTeams?.length ? (
            <Dropdown
              className="mobile-control-dropdown"
              placeholder="Select a team"
              fluid
              selection
              options={leadershipTeams.map(team => ({
                key: team.id,
                value: team.id,
                text: team.name,
              }))}
              value={teamId ?? undefined}
              onChange={(_event: React.SyntheticEvent<HTMLElement>, dropDownData: DropdownProps) => {
                const nextTeamId = dropDownData.value as string | number | undefined;
                handleTeamChange(nextTeamId);
              }}
              style={{
                width: '100%',
                border: 'none',
                boxShadow: 'none',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '18px',
                fontWeight: 550,
                minHeight: '40px',
                lineHeight: '20px',
                paddingLeft: '10px',
                paddingTop: '6px',
                paddingBottom: '6px',
              }}
            />
          ) : undefined
        }
        periodDropdown={
          <MobileAssessmentPeriodList
            className="mobile-control-dropdown"
            currentOrganizationId={currentOrganizationId}
            selectedAssessmentPeriodId={selectedAssessmentPeriodId}
            onChange={handleAssessmentPeriodChange}
          />
        }
      />
    );

    return () => setTopBarContent(null);
  }, [
    currentOrganizationId,
    handleAssessmentPeriodChange,
    handleTeamChange,
    leadershipTeams,
    selectedAssessmentPeriodId,
    selectedTeamId,
    setTopBarContent,
    teamId,
  ]);

  const handleAdd = async (recommendation: Recommendation) => {
    if (!assessmentPeriodId || !organizationId) {
      return;
    }

    if (!isMostRecentClosedAssessmentPeriod) {
      addToast('You can only add focus plan items in the latest closed assessment period.', {
        appearance: 'error',
        autoDismiss: true,
      });
      return;
    }

    const { leaderId, categoryUid } = determineLeaderAndCategory();

    dispatch(addRecommendationToPlan(recommendation));

    const result = await saveAllTasks([recommendation], leaderId, categoryUid);
    if (!result.success) {
      addToast(result.error || 'Failed to add focus area', {
        appearance: 'error',
        autoDismiss: true,
      });
      return;
    }

    addToast('Focus area added to your plan', {
      appearance: 'success',
      autoDismiss: true,
    });
  };

  const loading = !shouldFetchThemeDetail || isLoading || isFetching || isSaving;
  const themes = shouldFetchThemeDetail ? (data?.themes ?? []) : [];

  return (
    <MobilePageContent>
      <MobilePageSection>
        <SectionHeader>
          <Title>Build Your Focus Plan</Title>
        </SectionHeader>
        <SectionDescription>
          The themes below are prominent in your team's feedback. Review recommendations and add focus areas for your team.
        </SectionDescription>
      </MobilePageSection>

      <MobilePageSection padtop>
        {loading ? (
          <MobileSpinner text="Loading focus plan recommendations..." />
        ) : isError ? (
          <StateCard>Unable to load focus plan recommendations.</StateCard>
        ) : themes.length === 0 ? (
          <StateCard>No themes available for the selected assessment period yet.</StateCard>
        ) : (
          <ThemeList>
            {themes.map((theme: any, index: number) => {
              const sentiment = sentimentForSignal(theme.signal_strength);
              const accentColor = getThemeAccentColor(sentiment.variant);
              const title = theme.theme_title || `Theme ${index + 1}`;
              const count = typeof theme.feedback_count === 'number' ? theme.feedback_count : 0;

              const recommendations = Array.isArray(theme.suggested_interventions)
                ? theme.suggested_interventions.map(convertSuggestedInterventionToRecommendation)
                : [];

              return (
                <ThemeCard key={theme.theme_uid || `${title}-${index}`}>
                  <ThemeHeader $accentColor={accentColor}>
                    <ThemeTitleIcon variant={sentiment.variant}>
                      <Icon
                        name={
                          sentiment.variant === 'positive' || sentiment.variant === 'very-positive'
                            ? 'thumbs up'
                            : sentiment.variant === 'neutral'
                              ? 'comment alternate'
                              : 'warning sign'
                        }
                      />
                    </ThemeTitleIcon>
                    <ThemeTextGroup>
                      <ThemeTitle>{title}</ThemeTitle>
                      {theme.theme_description ? <ThemeDescription>{theme.theme_description}</ThemeDescription> : null}
                      {count > 0 ? <ThemeMeta>{count} feedback response{count === 1 ? '' : 's'}</ThemeMeta> : null}
                    </ThemeTextGroup>
                  </ThemeHeader>

                  <ThemeChildren $accentColor={accentColor}>
                    <MobileRecommendationTabContent
                      recommendations={recommendations}
                      pillarName={title}
                      onAdd={handleAdd}
                      selectedActionPlanTaskKeys={selectedActionPlanTaskKeys}
                    />
                  </ThemeChildren>
                </ThemeCard>
              );
            })}
          </ThemeList>
        )}
      </MobilePageSection>
    </MobilePageContent>
  );
};

export default MobileFocusPlanRecommendations;

const StateCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #ffffff;
  color: #475569;
  font-size: 14px;
  line-height: 1.55;
  padding: 18px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow-wrap: anywhere;
`;

const ThemeList = styled.div`
  display: grid;
  gap: 20px;
  width: 100%;
  min-width: 0;
  padding-left: 8px;
  padding-right: 8px;
  box-sizing: border-box;
`;

const ThemeCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  &:not(:last-child) {
    padding-bottom: 26px;
  }
`;

const ThemeHeader = styled.div<{ $accentColor: string }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  min-width: 0;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-left: 4px solid ${props => props.$accentColor};
  border-radius: 12px;
  padding: 11px 12px;
  box-sizing: border-box;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
`;

const ThemeChildren = styled.div<{ $accentColor: string }>`
  margin-left: 12px;
  padding: 10px 0 0 10px;
  width: calc(100% - 12px);
  min-width: 0;
  box-sizing: border-box;
`;

const ThemeTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`;

const ThemeTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  overflow-wrap: anywhere;
`;

const ThemeDescription = styled.p`
  margin: 0;
  color: #475569;
  font-size: 13px;
  line-height: 1.45;
  overflow-wrap: anywhere;
`;

const ThemeMeta = styled.div`
  color: #4b5563;
  font-size: 12px;
  font-weight: 500;
`;